/**
 * Remove Ritualist from Community Hook
 * Handles removing a ritualist from a community with proper cache invalidation
 */

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { removeRitualistFromCommunity } from "../services/community.service"

interface UseRemoveRitualistParams {
  communityId: string
}

/**
 * Remove Ritualist Mutation Hook
 * Removes a ritualist from a community and handles cache invalidation
 */
export function useRemoveRitualist({ communityId }: UseRemoveRitualistParams) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userId: string) => removeRitualistFromCommunity(communityId, userId),
    onSuccess: data => {
      // Invalidate the community ritualists query to refetch the list
      queryClient.invalidateQueries({
        queryKey: ["communityRitualists", communityId],
      })
      // Invalidate highlighted ritualists (if removed ritualist was highlighted)
      queryClient.invalidateQueries({
        queryKey: ["communityRitualists", communityId, true],
      })
      // Invalidate community detail
      queryClient.invalidateQueries({
        queryKey: ["community", communityId],
      })
      // Invalidate community statistics
      queryClient.invalidateQueries({
        queryKey: ["community-statistics", communityId],
      })
      toast.success(data.message || "Ritualist removed from community")
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || "Failed to remove ritualist")
    },
  })
}
